import React, { useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ExternalLink, Sparkles, MapPin, Megaphone, Download, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { fileUrl, fileDownloadUrl, api } from '@/lib/api';
import AvatarFrame from '@/components/AvatarFrame';
import PostReactions from '@/components/PostReactions';
import ProductionProgressBar from '@/components/ProductionProgressBar';
import EditPostDialog from '@/components/EditPostDialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { usePostViewTracker, trackClick } from '@/lib/analytics';
import { toast } from 'sonner';

// Mirrors POST_EDIT_WINDOW_MINUTES in backend/routers/posts.py. Kept as a
// module-level constant so the "Edit" menu item hides itself the moment the
// window expires — no point offering a button that can only return 403.
const POST_EDIT_WINDOW_MINUTES = 15;

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function resolveAuthor(post, currentUser) {
  // 1. Server-hydrated author (preferred)
  if (post.author && post.author.username) return post.author;
  // 2. If this is the current user's own post, fall back to their own auth profile.
  //    This prevents the "unknown user" flash when a post is prepended via WS or
  //    optimistic update before server hydration completes.
  if (currentUser && post.author_id === currentUser.id) {
    return {
      id: currentUser.id,
      username: currentUser.username,
      display_name: currentUser.display_name || currentUser.username,
      avatar_file_id: currentUser.avatar_file_id,
      avatar_frame_style: post.author?.avatar_frame_style || 'gilded_oval',
      avatar_frame_overlay_url: post.author?.avatar_frame_overlay_url,
    };
  }
  // 3. Last-resort placeholder (deleted account / hydration failure).
  const idShort = (post.author_id || 'anon').slice(0, 6);
  return { username: idShort, display_name: 'Unknown user' };
}

export default function FeedCard({ post: incomingPost, onTagClick, sourceFeed, sourceHashtag }) {
  const { user: currentUser } = useAuth();
  // Phase 24 — allow this card to mutate its own view of the post after an
  // author edit / delete without forcing every parent (Feed, Profile,
  // PostDetail, Communities, …) to thread an onUpdated callback. We overlay
  // the local patch on top of the prop so a fresh parent re-render still
  // wins if the server-truth ever diverges.
  const [localPatch, setLocalPatch] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const post = useMemo(() => (localPatch ? { ...incomingPost, ...localPatch } : incomingPost), [incomingPost, localPatch]);
  const author = resolveAuthor(post, currentUser);
  const frameVariant = author.avatar_frame_style || 'gilded_oval';
  const frameOverlay = author.avatar_frame_overlay_url || null;
  const cardRef = useRef(null);
  const isAuthor = !!(currentUser && post.author_id === currentUser.id);
  const isAdmin = !!(currentUser && (currentUser.roles || []).some((r) => r === 'MOD' || r === 'ISPA_ADMIN'));
  // Edit window check — hides the "Edit" menu item the instant the 15-minute
  // window expires so users don't click through to a guaranteed 403.
  const canEdit = useMemo(() => {
    if (!isAuthor || !post.created_at) return false;
    const created = new Date(post.created_at).getTime();
    return (Date.now() - created) < POST_EDIT_WINDOW_MINUTES * 60 * 1000;
  }, [isAuthor, post.created_at]);

  usePostViewTracker(cardRef, post, {
    sourceFeed,
    sourceHashtag,
    communityId: post && post.community_id,
    placement: 'feed',
  });

  const handleDelete = async () => {
    if (!isAuthor && !isAdmin) return;
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await api.delete(`/posts/${post.id}`);
      setDeleted(true);
      toast.success('Post deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // If the user deleted their post, we hide the card outright rather than
  // leaving an empty husk sitting in the feed.
  if (deleted) return null;

  return (
    <article
      ref={cardRef}
      className="group relative overflow-hidden rounded-[22px] border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] p-4 shadow-[var(--shadow-elev-1)] transition-shadow hover:shadow-[var(--shadow-elev-2)]"
      data-testid="feed-card"
    >
      {/* feed accent left rail */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full"
        style={{ background: 'linear-gradient(180deg, hsl(var(--feed-accent) / 0.85), hsl(var(--gold) / 0.45))' }}
      />
      <header className="flex items-center gap-3">
        <AvatarFrame user={author} size="sm" variant={frameVariant} overlayUrl={frameOverlay} dataTestId="feed-card-avatar" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Link to={`/u/${author.username}`} className="truncate text-sm font-medium" data-testid="feed-card-username">
              {author.display_name || author.username}
            </Link>
            <span className="font-mono text-xs text-muted-foreground">@{author.username}</span>
          </div>
          <div className="font-mono text-xs text-muted-foreground" data-testid="feed-card-timestamp">
            {relTime(post.created_at)} ago
            {post.edited_at && (
              <span
                className="ml-2 rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] px-1.5 py-0.5 text-[10px] font-mono tracking-[0.08em] text-muted-foreground"
                title={`Edited ${new Date(post.edited_at).toLocaleString()}`}
                data-testid="feed-card-edited-badge"
              >
                edited · {relTime(post.edited_at)} ago
              </span>
            )}
          </div>
        </div>
        {post.is_sponsored && (
          <span className="rounded-full border border-[hsl(var(--gold)/0.45)] bg-[hsl(var(--gold)/0.10)] px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(var(--gold-dim))]" data-testid="feed-card-sponsored">Sponsored</span>
        )}
        {post.is_featured && <Sparkles className="h-4 w-4 feed-accent-text" />}
        {(isAuthor || isAdmin) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="rounded-full p-1.5 text-muted-foreground hover:bg-[hsl(var(--app-card-2))] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--feed-accent)/0.5)]"
                aria-label="Post actions"
                data-testid="feed-card-author-menu"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              {isAuthor && canEdit && (
                <DropdownMenuItem onSelect={() => setEditOpen(true)} data-testid="feed-card-edit-action">
                  <Pencil className="mr-2 h-4 w-4" /> Edit post
                </DropdownMenuItem>
              )}
              {isAuthor && !canEdit && (
                <DropdownMenuItem
                  disabled
                  className="text-muted-foreground"
                  data-testid="feed-card-edit-expired"
                  title={`Edits are only allowed within ${POST_EDIT_WINDOW_MINUTES} minutes of posting`}
                >
                  <Pencil className="mr-2 h-4 w-4" /> Edit window expired
                </DropdownMenuItem>
              )}
              {(isAuthor || isAdmin) && canEdit && <DropdownMenuSeparator />}
              {(isAuthor || isAdmin) && (
                <DropdownMenuItem
                  onSelect={handleDelete}
                  className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]"
                  data-testid="feed-card-delete-action"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete post
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>

      {post.text && (
        <Link to={`/post/${post.id}`} className="mt-3 block whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/95">
          {post.text}
        </Link>
      )}

      {Array.isArray(post.media) && post.media.length > 0 && (
        <div className={`mt-3 grid gap-2 ${post.media.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {post.media.slice(0, 4).map((m) => (
            <div key={m.id} className="relative overflow-hidden rounded-2xl border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))]">
              {(m.content_type || '').startsWith('image/') ? (
                <img src={fileUrl(m.id)} alt="" className="h-full w-full object-cover" />
              ) : (m.content_type || '').startsWith('video/') ? (
                <video src={fileUrl(m.id)} controls className="h-full w-full" />
              ) : (
                <a href={fileUrl(m.id)} className="flex items-center gap-2 p-3 text-sm" target="_blank" rel="noreferrer">
                  <ExternalLink className="h-4 w-4" /> {m.original_filename || 'attachment'}
                </a>
              )}
              {post.allow_downloads && (
                <a
                  href={fileDownloadUrl(m.id, post.id)}
                  // Phase 25 — Android Chrome reliability: omit target="_blank"
                  // and set the `download` attribute so the browser hands the
                  // transfer off to the OS-level download manager from the
                  // current tab. Once handoff happens the download survives
                  // the user switching apps, the tab being suspended, or the
                  // browser being killed by the OS — none of which the
                  // previous "open in new tab" anchor could survive.
                  download={m.original_filename || ''}
                  rel="noreferrer"
                  className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white backdrop-blur hover:bg-black/80"
                  title={`Download ${m.original_filename || 'file'} — runs in the background even if you switch apps`}
                  data-testid="feed-card-download-button"
                >
                  <Download className="h-3 w-3" /> {m.original_filename ? 'Save' : 'Download'}
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {Array.isArray(post.links) && post.links.length > 0 && (
        <div className="mt-3 space-y-2">
          {post.links.map((l) => (
            <a
              key={`lnk-${l.url || l.title || ''}`}
              href={l.url}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackClick(post.id, l.url, { sourceFeed, sourceHashtag, communityId: post.community_id })}
              className="flex items-center gap-2 rounded-xl border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] px-3 py-2 text-sm hover:border-[hsl(var(--feed-accent)/0.5)]"
            >
              <ExternalLink className="h-4 w-4 feed-accent-text" />
              <span className="truncate">{l.title || l.url}</span>
              {l.site && <span className="ml-auto font-mono text-[11px] text-muted-foreground">{l.site}</span>}
            </a>
          ))}
        </div>
      )}

      {post.location?.coordinates && (
        <div className="mt-3 inline-flex items-center gap-1 rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card-2))] px-2 py-1 font-mono text-[11px] text-muted-foreground">
          <MapPin className="h-3 w-3" />
          {post.region_label
            ? post.region_label
            : (post.distance_m != null ? `${(post.distance_m / 1000).toFixed(1)} km away` : `${post.location.coordinates[1].toFixed(2)}, ${post.location.coordinates[0].toFixed(2)}`)}
        </div>
      )}

      {Array.isArray(post.hashtags) && post.hashtags.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1 scrollbar-none">
          {post.hashtags.map((h) => (
            <button key={`tag-${h}`} onClick={() => onTagClick && onTagClick(h)} className="shrink-0 rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-bg))] px-2.5 py-1 font-mono text-xs text-foreground hover:feed-accent-border" data-testid="feed-card-hashtag-chip">
              #{h}
            </button>
          ))}
        </div>
      )}

      <footer className="mt-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Link to={`/post/${post.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--app-border))] bg-[hsl(var(--app-card))] px-2.5 py-1 text-xs font-mono hover:text-foreground hover:border-[hsl(var(--accent)/0.40)]" data-testid="feed-card-reply-button">
            <MessageCircle className="h-4 w-4" /> <span className="tabular-nums">{post.comment_count || 0}</span>
          </Link>
          <PostReactions
            postId={post.id}
            initial={{
              kind: post.my_reaction || null,
              like_count: post.like_count || 0,
              dislike_count: post.dislike_count || 0,
            }}
          />
          {(post.hashtags || []).length > 0 && (
            <Link
              to={`/post/${post.id}/blast`}
              title="Support & blast this post — pay to push it to more hashtag followers"
              className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--gold)/0.45)] bg-[hsl(var(--accent)/0.06)] px-2.5 py-1 text-xs font-mono text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent)/0.12)] transition-colors"
              data-testid="feed-card-blast-button"
            >
              <Megaphone className="h-4 w-4 feed-accent-text" /> <span className="hidden sm:inline">Blast</span>
            </Link>
          )}
        </div>
        <span className="font-mono text-[11px] text-muted-foreground">{(post.feed_targets || []).join(' · ')}</span>
      </footer>
      {isAuthor && (
        <EditPostDialog
          open={editOpen}
          post={post}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => setLocalPatch(updated)}
        />
      )}
    </article>
  );
}
